import { useContext, useEffect, useMemo } from "react";
import docStore from "~/stores/document";
import { Document } from "~/document";
import styles from "./styles.module.scss";
import cn from "classnames";
import DocItem from "~/components/DocItem";
import DocListSkeleton from "~/components/DocItem/DocItemSkeleton";
import { Empty, EmptyHeader, EmptyTitle, EmptyDescription } from "~/components/ui/empty";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "~/components/ui/pagination";
import { observer } from "mobx-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/components/ui/select";
import DocumentsContext from "./DocumentsContext";

type SortOption = { label: string; value: keyof Document };

const sortOptions: SortOption[] = [
  { label: "Sort by time", value: "publication_date" },
  { label: "Sort by expiration", value: "expiration_date" },
];

// Windowed list of page numbers (1, current±1, last) with "ellipsis" gaps.
const getPageItems = (current: number, total: number): (number | "ellipsis")[] => {
  const items: (number | "ellipsis")[] = [];
  const left = Math.max(2, current - 1);
  const right = Math.min(total - 1, current + 1);
  items.push(1);
  if (left > 2) items.push("ellipsis");
  for (let i = left; i <= right; i++) items.push(i);
  if (right < total - 1) items.push("ellipsis");
  if (total > 1) items.push(total);
  return items;
};

const SearchResultsList = observer(() => {
  const { onDocumentClick, searchQuery, onSearch } = useContext(DocumentsContext);

  useEffect(() => {
    return () => {
      // reset search list on unmount
      docStore.clearSearch();
    };
  }, []);

  const items: Document[] = docStore.searchResult.results;
  const renderedItems = useMemo(() => {
    if (docStore.searching) {
      return <DocListSkeleton />;
    }
    if (!items || items.length === 0) {
      return (
        <Empty className={styles.noResultsMsg}>
          <EmptyHeader>
            <EmptyTitle>Nothing here</EmptyTitle>
            <EmptyDescription>Search returned no results</EmptyDescription>
          </EmptyHeader>
        </Empty>
      );
    }

    const currItemId = docStore.currentDocument !== null ? docStore.currentDocument.protocol : null;

    return (
      <div className={styles.searchResultContainer}>
        {items.map((item) => {
          const isSelected = currItemId !== null && item.protocol === currItemId;
          return (
            <DocItem
              key={item.protocol}
              doc={item}
              onDocumentClick={() => onDocumentClick(item)}
              className={cn({
                [styles.sidebarItem]: true,
                [styles.sidebarItem_active]: isSelected,
              })}
            />
          );
        })}
      </div>
    );
  }, [items, docStore.searching, docStore.currentDocument]);

  const resultsFrom = (docStore.searchResult.current_page - 1) * docStore.searchResult.page_size;
  const resultsTo = docStore.searchResult.current_page * docStore.searchResult.page_size;
  const resultsTotal = docStore.searchResult.count;

  const pageSize = searchQuery.pageSize || 25;
  const pageCount = Math.ceil(resultsTotal / pageSize);
  const currentPage = searchQuery.page || 1;

  const goToPage = (page: number) => {
    if (page < 1 || page > pageCount || page === currentPage) return;
    onSearch(searchQuery.set("page", page).set("pageSize", pageSize));
  };

  return (
    <div className={styles.leftColContent}>
      <div className={styles.searchResultsHeader}>
        <Select
          value={searchQuery.sortByField}
          onValueChange={(v) => onSearch(searchQuery.set("sortByField", v as keyof Document))}
        >
          <SelectTrigger className={styles.sorter}>
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            {sortOptions.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!!docStore.searchResult.current_page && (
          <i className="text-xs italic [color:var(--text-02)]">
            Showing {resultsFrom}-{resultsTo} of {resultsTotal} results
          </i>
        )}
      </div>
      <div className="flex-[1_1_100%]">{renderedItems}</div>
      {pageCount > 1 && (
        <Pagination className="mt-4 justify-end">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                className={cn("cursor-pointer", currentPage <= 1 && "pointer-events-none opacity-50")}
                onClick={(e) => {
                  e.preventDefault();
                  goToPage(currentPage - 1);
                }}
              />
            </PaginationItem>
            {getPageItems(currentPage, pageCount).map((item, i) =>
              item === "ellipsis" ? (
                <PaginationItem key={`ellipsis-${i}`}>
                  <PaginationEllipsis />
                </PaginationItem>
              ) : (
                <PaginationItem key={item}>
                  <PaginationLink
                    isActive={item === currentPage}
                    className="cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      goToPage(item);
                    }}
                  >
                    {item}
                  </PaginationLink>
                </PaginationItem>
              )
            )}
            <PaginationItem>
              <PaginationNext
                className={cn("cursor-pointer", currentPage >= pageCount && "pointer-events-none opacity-50")}
                onClick={(e) => {
                  e.preventDefault();
                  goToPage(currentPage + 1);
                }}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
});

export default SearchResultsList;
